import { useEffect, useMemo, useState } from "react";
import { Modal, Alert, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import OrganizerTimePillSelect from "../../components/OrganizerTimePillSelect";
import { SingleDatePickerPill } from "../../../pages/reservas/components/ReservaShared";
import { organizerCreateReservationBlockApi } from "../../../api/api";
import { SPORT_LABELS } from "../../../constants/courtDomain";
import {
	BLOCK_TYPE_OPTIONS,
	DEFAULT_BLOCK_TYPE,
	validateCourtBlockForm,
} from "./courtBlockModel";
import "../../components/EventFormPickers.css";
import "./NewInternalReservationModal.css";
import "./NewCourtBlockModal.css";

const DATE_FORMAT = "YYYY-MM-DD";
const DEFAULT_SLOT_START = "09:00";
const DEFAULT_SLOT_END = "10:00";

const conflictDate = (isoDate) => {
	const day = dayjs(isoDate);
	return day.isValid() ? day.format("DD/MM") : isoDate || "-";
};

/*
 * "Bloquear canchas" (club organizer): one request creates a typed block for
 * one, several or all courts, for one day or a date range, for the whole day or
 * a time range (POST /api/organizer/reservations/block). The backend refuses
 * the request with a 409 and the list of colliding reservations when any active
 * reservation overlaps, and creates nothing; the modal then keeps the form so
 * the organizer can adjust it. The client rules in courtBlockModel only give
 * feedback before the request.
 */
export default function NewCourtBlockModal({
	show,
	bar,
	courts = [],
	courtsState = "ready",
	onRetryCourts,
	defaultDate,
	onHide,
	onCreated,
}) {
	const clubId = bar?._id || null;
	const initialDate = useMemo(() => {
		const parsed = dayjs(defaultDate);
		return (parsed.isValid() ? parsed : dayjs()).format(DATE_FORMAT);
	}, [defaultDate]);

	// The viewed day can be in the past, so the pickers start at the earlier of
	// today and that day. A block can be set up to a year ahead.
	const minDate = useMemo(() => {
		const today = dayjs().startOf("day");
		const viewed = dayjs(initialDate).startOf("day");
		return viewed.isBefore(today) ? viewed : today;
	}, [initialDate]);
	const maxDate = useMemo(() => dayjs().startOf("day").add(365, "day"), []);

	const [type, setType] = useState(DEFAULT_BLOCK_TYPE);
	const [courtMode, setCourtMode] = useState("all");
	const [courtIds, setCourtIds] = useState([]);
	const [dateFrom, setDateFrom] = useState(initialDate);
	const [dateTo, setDateTo] = useState(initialDate);
	const [allDay, setAllDay] = useState(false);
	const [slotStart, setSlotStart] = useState(DEFAULT_SLOT_START);
	const [slotEnd, setSlotEnd] = useState(DEFAULT_SLOT_END);
	const [reason, setReason] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState("");
	const [conflicts, setConflicts] = useState(null);

	const resetForm = () => {
		setType(DEFAULT_BLOCK_TYPE);
		setCourtMode("all");
		setCourtIds([]);
		setDateFrom(initialDate);
		setDateTo(initialDate);
		setAllDay(false);
		setSlotStart(DEFAULT_SLOT_START);
		setSlotEnd(DEFAULT_SLOT_END);
		setReason("");
		setSubmitError("");
		setConflicts(null);
	};

	useEffect(() => {
		if (show) resetForm();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, initialDate]);

	const handleHide = () => {
		if (submitting) return;
		resetForm();
		onHide?.();
	};

	// Hasta follows Desde while the block is still a single day, and is pulled
	// forward when Desde moves past it.
	const handleDateFromChange = (value) => {
		setDateTo((current) =>
			current === dateFrom || current < value ? value : current,
		);
		setDateFrom(value);
	};

	const toggleCourt = (courtId) =>
		setCourtIds((current) =>
			current.includes(courtId)
				? current.filter((id) => id !== courtId)
				: [...current, courtId],
		);

	const errors = validateCourtBlockForm({
		dateFrom,
		dateTo,
		allDay,
		slotStart,
		slotEnd,
		courtMode,
		courtIds,
	});
	const canSubmit =
		Boolean(clubId) && Object.keys(errors).length === 0 && !submitting;

	const noCourtsYet = courts.length === 0;
	const courtsLoading = courtsState === "loading" && noCourtsYet;
	const courtsFailed = courtsState === "error" && noCourtsYet;

	const handleSubmit = async () => {
		if (!canSubmit) return;
		const payload = {
			barId: clubId,
			// Courts in grid order; none means the whole club.
			...(courtMode === "some"
				? { courtIds: courts.filter((c) => courtIds.includes(c.id)).map((c) => c.id) }
				: {}),
			dateFrom,
			dateTo,
			...(allDay ? {} : { slotStart, slotEnd }),
			type,
			...(reason.trim() ? { reason: reason.trim() } : {}),
		};

		setSubmitting(true);
		setSubmitError("");
		setConflicts(null);
		try {
			const response = await organizerCreateReservationBlockApi(payload);
			await onCreated?.(response?.data?.info || null);
			resetForm();
			onHide?.();
		} catch (err) {
			const data = err?.response?.data;
			if (err?.response?.status === 409) {
				const list = Array.isArray(data?.conflicts) ? data.conflicts : [];
				setSubmitError(
					data?.message ||
						"Hay reservas en ese horario. Cancelalas o movelas antes de bloquear.",
				);
				setConflicts({
					list,
					total: Math.max(Number(data?.conflictCount) || 0, list.length),
				});
			} else {
				setSubmitError(
					data?.message || "No pudimos crear el bloqueo. Intentá de nuevo.",
				);
			}
		} finally {
			setSubmitting(false);
		}
	};

	const hiddenConflicts = conflicts
		? conflicts.total - conflicts.list.length
		: 0;

	return (
		<Modal
			show={show}
			onHide={handleHide}
			centered
			className="new-internal-reservation-modal new-court-block-modal"
		>
			<Modal.Header closeButton>
				<Modal.Title>
					Bloquear canchas{bar?.name ? ` | ${bar.name}` : ""}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{submitError && (
					<Alert variant="danger" role="alert">
						<div>{submitError}</div>
						{conflicts && conflicts.list.length > 0 && (
							<>
								<ul className="ncb-conflicts" aria-label="Reservas en conflicto">
									{conflicts.list.map((conflict) => (
										<li
											key={
												conflict.reservationId ||
												`${conflict.code}-${conflict.date}-${conflict.slotStart}`
											}
										>
											<strong>{conflictDate(conflict.date)}</strong>
											<span>{conflict.courtName || "-"}</span>
											<span>
												{conflict.slotStart}-{conflict.slotEnd}
											</span>
											<span>{conflict.name || "Sin nombre"}</span>
											<span>#{conflict.code}</span>
										</li>
									))}
								</ul>
								{hiddenConflicts > 0 && (
									<div className="ncb-conflicts-more">y {hiddenConflicts} más</div>
								)}
							</>
						)}
					</Alert>
				)}

				<div className="zr-field">
					<span className="zr-field__label" id="ncb-type-label">
						Tipo
					</span>
					<div
						className="ncb-chips"
						role="radiogroup"
						aria-labelledby="ncb-type-label"
					>
						{BLOCK_TYPE_OPTIONS.map((option) => (
							<label
								key={option.value}
								className={`ncb-chip${type === option.value ? " is-active" : ""}`}
							>
								<input
									type="radio"
									name="ncb-type"
									value={option.value}
									checked={type === option.value}
									onChange={() => setType(option.value)}
								/>
								{option.label}
							</label>
						))}
					</div>
				</div>

				<div className="zr-field">
					<span className="zr-field__label" id="ncb-courts-label">
						Canchas
					</span>
					<div
						className="ncb-chips"
						role="radiogroup"
						aria-labelledby="ncb-courts-label"
					>
						<label className={`ncb-chip${courtMode === "all" ? " is-active" : ""}`}>
							<input
								type="radio"
								name="ncb-courts"
								value="all"
								checked={courtMode === "all"}
								onChange={() => setCourtMode("all")}
							/>
							Todas las canchas
						</label>
						<label className={`ncb-chip${courtMode === "some" ? " is-active" : ""}`}>
							<input
								type="radio"
								name="ncb-courts"
								value="some"
								checked={courtMode === "some"}
								onChange={() => setCourtMode("some")}
							/>
							Elegir canchas
						</label>
					</div>
					{courtMode === "some" &&
						(courtsLoading ? (
							<p className="ncb-note" role="status">
								Cargando canchas...
							</p>
						) : courtsFailed ? (
							<div className="ncb-note" role="alert">
								No pudimos cargar las canchas.{" "}
								{onRetryCourts && (
									<button
										type="button"
										className="zr-btn zr-btn--soft"
										onClick={onRetryCourts}
									>
										Reintentar
									</button>
								)}
							</div>
						) : noCourtsYet ? (
							<p className="ncb-note">No hay canchas activas para elegir.</p>
						) : (
							<div className="ncb-courts" role="group" aria-label="Elegir canchas">
								{courts.map((court) => (
									<label key={court.id} className="ncb-check">
										<input
											type="checkbox"
											checked={courtIds.includes(court.id)}
											onChange={() => toggleCourt(court.id)}
										/>
										<span>
											{court.name}
											{court.sport ? (
												<span className="ncb-court-sport">
													{" "}
													· {SPORT_LABELS[court.sport] || court.sport}
												</span>
											) : null}
										</span>
									</label>
								))}
							</div>
						))}
					{errors.courts && <span className="ncb-error">{errors.courts}</span>}
				</div>

				<div className="ncb-row">
					<div className="zr-field">
						<span className="zr-field__label">Desde</span>
						<SingleDatePickerPill
							value={dayjs(dateFrom)}
							onChange={(day) =>
								day?.isValid() && handleDateFromChange(day.format(DATE_FORMAT))
							}
							minDate={minDate}
							maxDate={maxDate}
							ariaLabel="Fecha desde"
						/>
					</div>
					<div className="zr-field">
						<span className="zr-field__label">Hasta</span>
						<SingleDatePickerPill
							value={dayjs(dateTo)}
							onChange={(day) => day?.isValid() && setDateTo(day.format(DATE_FORMAT))}
							minDate={minDate}
							maxDate={maxDate}
							ariaLabel="Fecha hasta"
						/>
					</div>
				</div>
				{errors.dates && <span className="ncb-error">{errors.dates}</span>}

				<div className="zr-field">
					<label className="ncb-check">
						<input
							type="checkbox"
							checked={allDay}
							onChange={(event) => setAllDay(event.target.checked)}
						/>
						<span>Todo el día</span>
					</label>
					{!allDay && (
						<div className="ncb-row">
							<div className="zr-field">
								<span className="zr-field__label">Hora desde</span>
								<OrganizerTimePillSelect
									value={slotStart}
									onChange={setSlotStart}
									triggerClassName="ef-date-pill-trigger"
									ariaLabel="Hora desde"
									quarterHours
								/>
							</div>
							<div className="zr-field">
								<span className="zr-field__label">Hora hasta</span>
								<OrganizerTimePillSelect
									value={slotEnd}
									onChange={setSlotEnd}
									triggerClassName="ef-date-pill-trigger"
									ariaLabel="Hora hasta"
									quarterHours
								/>
							</div>
						</div>
					)}
					{errors.time && <span className="ncb-error">{errors.time}</span>}
				</div>

				<div className="zr-field">
					<span className="zr-field__label">Motivo (opcional)</span>
					<input
						type="text"
						className="zr-input"
						placeholder="Ej: Torneo de otoño"
						aria-label="Motivo"
						value={reason}
						onChange={(event) => setReason(event.target.value)}
					/>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					className="btn-create-reservation"
					disabled={!canSubmit}
					onClick={handleSubmit}
				>
					{submitting ? <Spinner animation="border" size="sm" /> : "Crear bloqueo"}
				</button>
			</Modal.Footer>
		</Modal>
	);
}
